import { AxiosInstance } from "axios";
import { store, refreshAccessToken } from "@redux";
import { performLogoutActions } from "../Utilities/logoutUtils";
import { PATH_LOGIN } from "@routes";
import { ErrorToast } from "src/Components/toasts/ErrorToast";

/**
 * Shared JWT handling for every axios client that talks to the backend
 * (both the V2 API and the TDMS V1 proxy mounted under it).
 *
 * - Request: attach the app JWT when the user is logged in. Guests send no
 *   Authorization header at all; the backend decides per route whether that
 *   is acceptable.
 * - Response 401: refresh the JWT once (coordinated across all clients so a
 *   burst of failures triggers a single refresh) and retry. If the refresh
 *   fails, log out and send the user to the login page.
 * - Response 403 "Email verification required": surface a verify-email
 *   prompt and reject. The user stays logged in.
 */

const EMAIL_VERIFICATION_MESSAGE = "Please verify your email before continuing.";

export const isEmailVerificationError = (error: any): boolean => {
    if (error?.response?.status !== 403) {
        return false;
    }
    const body = error.response?.data;
    const text = [body?.title, body?.error, body?.message]
        .filter((v) => typeof v === "string")
        .join(" ")
        .toLowerCase();
    return text.includes("email verification");
};

/** Axios config fields we add to coordinate retries (kept off the wire — never in headers). */
interface IRetryMeta {
    _jwtRetried?: boolean;
    _jwtSent?: string;
}

const isOnLoginPage = () => window.location.pathname === PATH_LOGIN;

let isRefreshing = false;
let waiters: Array<{ resolve: () => void; reject: (reason?: any) => void }> = [];

const flushWaiters = (error: any = null) => {
    waiters.forEach((w) => (error ? w.reject(error) : w.resolve()));
    waiters = [];
};

/**
 * Refresh the JWT exactly once no matter how many clients/requests hit 401
 * concurrently. Resolves when a fresh token is in the store, rejects (after
 * logging the user out) when the refresh fails.
 */
const refreshJwtOnce = async (): Promise<void> => {
    if (isRefreshing) {
        return new Promise<void>((resolve, reject) => {
            waiters.push({ resolve, reject });
        });
    }
    isRefreshing = true;
    try {
        const state = store.getState();
        const userId = state.account.account?.id;
        await store
            .dispatch(
                refreshAccessToken({
                    refreshToken: state.auth?.jwt?.refreshToken,
                    userId,
                }),
            )
            .unwrap();
        flushWaiters();
    } catch (err) {
        console.error("Error refreshing token:", err);
        await performLogoutActions();
        flushWaiters(err);
        if (!isOnLoginPage()) {
            console.info("Redirecting to login page...");
            window.location.href = PATH_LOGIN;
        }
        throw err;
    } finally {
        isRefreshing = false;
    }
};

export const attachJwtAuthInterceptors = (client: AxiosInstance) => {
    client.interceptors.request.use((config) => {
        const state = store.getState();
        const accessToken = state.auth?.jwt?.accessToken;
        if (!config.headers.Authorization && accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
            (config as IRetryMeta)._jwtSent = accessToken;
        }
        return config;
    });

    client.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;
            const status = error.response?.status;

            if (isEmailVerificationError(error)) {
                ErrorToast({ Message: EMAIL_VERIFICATION_MESSAGE });
                return Promise.reject(error);
            }

            const meta = originalRequest as IRetryMeta | undefined;
            if (status === 401 && originalRequest && !meta?._jwtRetried) {
                const currentToken = store.getState().auth?.jwt?.accessToken;
                if (!currentToken) {
                    // Guest: nothing to refresh. The route simply needs a login.
                    return Promise.reject(error);
                }
                meta!._jwtRetried = true;
                // Only refresh if this request was sent with the token we still hold.
                // A request that went out with an older token (another 401 already
                // refreshed) just needs to be retried with the current one.
                if (!meta?._jwtSent || meta._jwtSent === currentToken) {
                    await refreshJwtOnce();
                }
                // Drop the stale header so the request interceptor attaches the new one.
                delete originalRequest.headers.Authorization;
                return client(originalRequest);
            }

            return Promise.reject(error);
        },
    );

    return client;
};
